/** OSM permission and direction, independent of terrain grade or surface.
 * https://wiki.openstreetmap.org/wiki/Key:bicycle
 * Unresolved conditions and destination-only access cannot support a through route.
 */
import { reviewedBikeParkTrail } from "./bike-park-access";
import { PLANAI_CORE_TRAILS } from "./planai-core";
import { planaiTrailPass, type PlanaiTrailPass } from "./operations/planai";

export function mtbAccess(tags: Record<string, string>, osmId?: string): { forward: boolean; backward: boolean; reason: string | null; conditionalAccess?: "bike-park-pass-v1" | "bike-park-pass-v2"; bikeParkPass?: { validFrom: string; validTo: string } | PlanaiTrailPass } {
  const value = (key: string) => tags[key]?.trim().toLowerCase();
  const permitted = new Set(["yes", "designated", "official", "permissive"]);
  const access = value("bicycle") ?? value("vehicle") ?? value("access");
  const reviewed = access === "customers" && (!tags.operator || tags.operator === "Whistler Blackcomb") ? reviewedBikeParkTrail(osmId, tags.name) : null;
  if ((access && !permitted.has(access) && !reviewed) || value("highway") === "construction" || value("highway") === "proposed" || value("mtb") === "no") {
    return { forward: false, backward: false, reason: "Bicycle through access is restricted or unresolved" };
  }
  if (Object.keys(tags).some((key) => /^(access|vehicle|bicycle|oneway|oneway:bicycle)(:.*)?:conditional$/.test(key) && tags[key].trim())) {
    return { forward: false, backward: false, reason: "Conditional access needs an operator review" };
  }
  const direction = value("oneway:bicycle") ?? value("oneway") ?? (value("junction") === "roundabout" ? "yes" : "no");
  if (!["yes", "1", "true", "-1", "no", "0", "false"].includes(direction)) return { forward: false, backward: false, reason: "Direction is unresolved" };
  const directional = (key: string) => !value(key) || permitted.has(value(key)!);
  // A later ordinary ingestion must retain the exact Planai pass/calendar
  // marker even though the public OSM cycleway has no customer-only tag.
  // This never overrides any explicit denial or unresolved condition above.
  const planaiIdentity = PLANAI_CORE_TRAILS.find(trail => trail.osmId === osmId);
  if (planaiIdentity && planaiIdentity.name !== tags.name) return { forward: false, backward: false, reason: "Planai source name needs a fresh access review" };
  const planai = planaiIdentity ? planaiTrailPass(planaiIdentity.osmId) : undefined;
  return {
    forward: direction !== "-1" && directional("bicycle:forward"),
    backward: !["yes", "1", "true"].includes(direction) && directional("bicycle:backward"),
    reason: null,
    ...(reviewed ? { conditionalAccess: "bike-park-pass-v1" as const, bikeParkPass: reviewed.pass } : {}),
    ...(planai ? { conditionalAccess: "bike-park-pass-v2" as const, bikeParkPass: planai } : {}),
  };
}

export function cumulativeElevation(points: readonly { ele: number }[]): { ascentM: number; descentM: number } {
  let ascentM = 0, descentM = 0;
  for (let i = 1; i < points.length; i++) {
    const delta = points[i].ele - points[i - 1].ele;
    if (!Number.isFinite(delta)) continue;
    if (delta > 0) ascentM += delta;
    else descentM -= delta;
  }
  return { ascentM: Math.round(ascentM), descentM: Math.round(descentM) };
}
